import * as _ from "lodash";
import * as React from "react";
import * as H from "history";
import { match } from "react-router";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";

import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";
import { Table } from "src/core/dashboard/components/widgets";
import {
  IUser,
  UpdateUserParams,
  useUserApi
} from "src/core/users/api/users.api";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import {
  ICustomer,
  useCustomerApi
} from "src/core/customers/api/customers.api";
import { getUserAssignedCustomerTableColumns } from "src/core/shared/components/tables/userAssignedCustomersTableColumns";

import { UrlParams } from "../UserDrops/CustomerUserDropAssignmentPage";
import { CustomerAssignmentTabs } from "./UserCustomerAssignmentPage";
import DefaultCustomer from "../DefaultCustomer";

type TabProps = {
  user: IUser;
  history: H.History;
  terminology: ITerminology;
  match: match<UrlParams> | null;
  reloadUser: () => void;
  // Callback returns the desired tab to make active
  updateActiveTab: (name: CustomerAssignmentTabs) => void;
};

const defaultPagination: PaginatedParams<ICustomer> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "accountNumber", "DESC" ] ]
};

export type SelectableCustomer = ICustomer & {selected: boolean};

/**
 * customers assigned to user, displayed in a table with default branch
 *
 * @returns
 */
const UserAssignedCustomerTab: React.ReactFCWithChildren<TabProps> = props => {
  const {
    terminology, user, history, reloadUser, updateActiveTab
  } = props;

  const TableRef = React.useRef<TableRefProps>(null);
  const [ updateUserResponse, updateUser ] = useUserApi("UPDATE_USER");
  const [ userCustomerResponse, getUserCustomers ] = useCustomerApi("LIST_USER_CUSTOMERS");
  const [ reloadUserFlag, setReloadUserFlag ] = React.useState(false);
  const [ availableCustomer, setAvailableCustomer ] = React.useState<SelectableCustomer[]>([]);
  const [ loading, setLoading ] = React.useState(false);
  const [ showTable, setShowTable ] = React.useState(true);
  const [ isFiltering, setIsFiltering ] = React.useState(false);

  //loading is set true by buttons too, this is so there is an instant reaction to interactions.
  React.useEffect(() => {
    setLoading(userCustomerResponse.pending);
  }, [ userCustomerResponse ]);

  React.useEffect(() => {
    const filtersToCheck = { ...userCustomerResponse.params?.filters };

    delete filtersToCheck.id;

    if (!_.isEmpty(filtersToCheck)) {
      setIsFiltering(true);
    } else {
      setIsFiltering(false);
    }
  }, [ userCustomerResponse.params?.filters ]);

  //getting the customers out of the response and mapping them to have a selected property
  React.useEffect(() => {
    setAvailableCustomer(() => {
      //using ids on the backend so filtering default customer on front end
      const customers = userCustomerResponse.data?.result ? userCustomerResponse.data?.result as SelectableCustomer[] : [];

      return customers.map(customer => ({
        ...customer,
        selected: false
      }));
    });
  }, [ user.defaultCustomerId, userCustomerResponse.data?.result ]);

  //determine if we should show the table if no data is present.
  React.useEffect(() => {
    if (userCustomerResponse.data?.result && userCustomerResponse.data?.result.length < 1 && !isFiltering) {
      setShowTable(false);
    }
  }, [ isFiltering, userCustomerResponse.data?.result ]);

  //if any errors
  React.useEffect(() => {
    if (updateUserResponse.error) {
      toast.error(`Could not save ${terminology.user.singular.text}. Please try again.`);
    }

    if (userCustomerResponse.error) {
      toast.error(`Could not retrieve ${terminology.customer.plural.text}. Please try again`);
    }
  }, [
    terminology,
    updateUserResponse.error,
    userCustomerResponse.error
  ]);

  // this is used to trigger the reloading of the user in the parent component, there user data comes from there.
  React.useEffect(() => {
    if (updateUserResponse.complete) {
      if (reloadUserFlag) {
        reloadUser();
        setReloadUserFlag(false);
      }
    }
    //only want to trigger this when the response is updated not the flag
  }, [
    reloadUser,
    updateUserResponse,
    reloadUserFlag
  ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<ICustomer>, pagination: PaginatedParams<ICustomer>}) => {
      const filters = params.filters;

      //we dont want the default branch in the table data
      filters.id = { $not: user.defaultCustomerId };

      getUserCustomers({
        userId: user.id,
        pagination: params.pagination,
        filters
      });
    },
    [ user, getUserCustomers ]
  );

  //handles sending the multiple restrict request.
  const handleBulkRestrict = React.useCallback(() => {
    setAvailableCustomer(prevCustomers => {
      if (!user || !user.userCustomers) {
        return prevCustomers;
      }

      if (prevCustomers.length < 1) {
        toast.error(`Please select at least 1 ${terminology.customer.singular.text}`);

        return prevCustomers;
      }

      const selectedCustomers = prevCustomers.filter(customer => customer.selected);
      const selectedCustomersIds = selectedCustomers.map(customer => customer.id);
      const newCustomers = user.userCustomers.filter(customer => !selectedCustomersIds.includes(customer.id));

      updateUser({
        ...user,
        userCustomers: newCustomers
      } as UpdateUserParams);
      setReloadUserFlag(true);
      setLoading(true);

      //reset selected customers
      return [];
    });
  }, [
    user,
    updateUser,
    terminology
  ]);

  //for use in singular unassignment
  const unAssignCustomer = React.useCallback((customerId: number) => {
    if (!user || !user.userCustomers) {
      return;
    }
    const userCustomers = user.userCustomers;
    const toRemoveI = userCustomers.findIndex(customer => customer.id === customerId);

    if (toRemoveI > -1) {
      userCustomers.splice(toRemoveI, 1);
    }

    setLoading(true);

    updateUser({
      ...user,
      userCustomers
    } as UpdateUserParams);
    setReloadUserFlag(true);
  }, [
    setReloadUserFlag,
    user,
    updateUser
  ]);

  const setDefaultCustomer = React.useCallback((customerId: number) => {
    updateUser({
      ...user,
      //do not set the user branches
      userBranches: [],
      defaultCustomerId: customerId
    } as UpdateUserParams);
    setReloadUserFlag(true);
  },
  [
    setReloadUserFlag,
    updateUser,
    user
  ]
  );

  const columns = React.useMemo(() => {
    let isCustomerSelected = false;

    return getUserAssignedCustomerTableColumns({
      //action button per row.
      actionButton: info => {
        return (
          <ActionButton
            title="Actions"
            items={[
              {
                title: "View",
                action: () => {
                  history.push(`/admin/customers/${info.row.original.id}`);
                },
                newTabPath: `/admin/customers/${info.row.original.id}`
              },
              {
                title: "Set As Default",
                action: () => {
                  setDefaultCustomer(info.row.original.id);
                  setLoading(true);
                }
              },
              {
                title: "Restrict Access",
                icon: Icons.WARN,
                action: () => {
                  unAssignCustomer(info.row.original.id);
                }
              }
            ]}
          />
        );
      },
      //handler for the check box in each row
      checkboxAction: (customer: ICustomer) => {
        setAvailableCustomer(prevCustomer => {
          const customerExists = prevCustomer.findIndex(d => customer.id === d.id);
          const editedDrops = [ ...prevCustomer ];

          editedDrops[ customerExists ].selected = !editedDrops[ customerExists ].selected;
          isCustomerSelected = editedDrops.filter(customer => customer.selected).length ? true : false;

          return editedDrops;
        });
      },
      bulkActionButton: () => {
        return (
          <ActionButton
            title="Bulk Actions"
            items={[
              {
                title: "Restrict Access",
                icon: Icons.WARN,
                disabled: !isCustomerSelected,
                action: handleBulkRestrict
              },
              {
                title: "Select All",
                action: () => {
                  setAvailableCustomer(prevCustomer => {
                    const selectedDrops = prevCustomer.filter(customer => customer.selected === true);

                    if (selectedDrops.length === prevCustomer.length) {
                      isCustomerSelected = false;

                      return prevCustomer.map(customer => ({
                        ...customer,
                        selected: false
                      }));
                    }

                    isCustomerSelected = true;

                    return prevCustomer.map(customer => ({
                      ...customer,
                      selected: true
                    }));
                  });
                }
              }
            ]}
          />
        );
      },
      terminology

    });
  }, [
    terminology,
    history,
    setDefaultCustomer,
    unAssignCustomer,
    handleBulkRestrict
  ]);

  return (
    <AssignedDropsTabWrapper>
      <DefaultCustomer
        customerId={user.defaultCustomerId}
        terminology={terminology}
      />

      {showTable ? (
        <>
          <Table
            ref={TableRef}
            tableOf={`${user.firstName} can also access the following ${terminology.customer.plural.text}`}
            error={userCustomerResponse.error}
            loading={loading}
            data={availableCustomer && !loading ? availableCustomer : []}
            onFetchData={onFetchData}
            defaultPagination={defaultPagination}
            pageCount={userCustomerResponse.data?.pages ? userCustomerResponse.data.pages : 0}
            rowCount={userCustomerResponse.data?.count ? userCustomerResponse.data.count : 0}
            columns={columns}
          />
        </>
      ) : (
        <div className="give-access">
          <Text as="h3">

            {` There are no other accessible ${terminology.customer.plural.text}  `}

          </Text>

          <Button
            onClick={() => {
              // Make the restricted tab active
              updateActiveTab(CustomerAssignmentTabs.Restricted);
            }}
          >
            Give Access
          </Button>
        </div>
      )}

    </AssignedDropsTabWrapper>
  );
};

const AssignedDropsTabWrapper = styled.div`
  ${({ theme }) => css`
    .give-access {
      display: flex;
      flex-direction: row;

      align-items: center;
      
      h3 {
      padding:${theme.spacing.padding.md};      
    }
    
    h3{
      margin-bottom: 0px
    }
  `}
`;

export default UserAssignedCustomerTab;
